import { Katalog, SkillWithRating } from './katalog.model';

/**
 * NP20-889: Eine Bewertung des Beraters fuer genau ein Katalog-Item, adressiert ueber die
 * stabile uuid des Items im Master-Katalog.
 */
export interface CatalogItemRating {
  itemUuid: string;
  rating: number;
}

/**
 * NP20-889: Zuweisung eines Katalogs an einen Berater. Traegt bewusst keinen Katalogtext —
 * Name und Item-Bezeichnungen kommen live aus dem Master-Katalog. Die Bewertungen sind sparse:
 * fehlt ein Item, ist es unbewertet (0).
 */
export interface CatalogAssignment {
  catalogId: string;
  ratings: CatalogItemRating[];
}

/**
 * Legt die Bewertungen einer Zuweisung auf den Master-Katalog und liefert eine Kopie, die sich
 * wie der frueher eingebettete Katalog verhaelt. Items ohne Bewertung bekommen 0, damit die
 * Pflichtfeld-Zaehler und die Sternedarstellung unveraendert weiterarbeiten.
 *
 * Es wird bewusst kopiert statt in den Master geschrieben: derselbe Master kann mehrfach
 * aufgeloest werden, und die Bearbeitung in SkillsBaComponent mutiert die Kenntnisse.
 */
export function applyRatings(master: Katalog, ratings: CatalogItemRating[]): Katalog {
  const byUuid = new Map(ratings.map((r) => [r.itemUuid, r.rating]));
  const kenntnisse: SkillWithRating[] = (master.kenntnisse ?? []).map((k) => ({
    ...k,
    bewertung: k.uuid ? (byUuid.get(k.uuid) ?? 0) : 0,
  }));
  return { ...master, kenntnisse };
}

/**
 * Anzeigereihenfolge der Kataloge: RVSE-Version, dann Rollenbezeichnung, dann Preisgruppe.
 * Frueher sortierte das Backend die am Berater eingebetteten Kataloge; die Zuweisungsliste
 * kommt in Schreibreihenfolge, deshalb sortiert das jetzt das Frontend.
 */
function byRvseThenRoleThenPriceGroup(a: Katalog, b: Katalog): number {
  return (
    (a.rvseVersion ?? 0) - (b.rvseVersion ?? 0) ||
    (a.rollenbezeichnung ?? '').localeCompare(b.rollenbezeichnung ?? '') ||
    (a.preisgruppe ?? 0) - (b.preisgruppe ?? 0)
  );
}

/**
 * Baut aus den Zuweisungen die anzeigbaren Kataloge. Zuweisungen ohne passenden Master werden
 * uebersprungen — das BE raeumt sie beim Loeschen eines Katalogs zwar kaskadierend weg, aber ein
 * fehlender Master darf die Ansicht nicht aufreissen.
 */
export function resolveAssignments(
  assignments: CatalogAssignment[],
  mastersById: Map<string, Katalog>,
): Katalog[] {
  return assignments
    .map((assignment) => {
      const master = mastersById.get(assignment.catalogId);
      return master ? applyRatings(master, assignment.ratings ?? []) : null;
    })
    .filter((katalog): katalog is Katalog => katalog !== null)
    .sort(byRvseThenRoleThenPriceGroup);
}

/**
 * Dreht die Anzeige-Kataloge zurueck in eine Zuweisung. Unbewertete Items bleiben weg — das BE
 * verwirft rating 0 ohnehin, und so bleibt die Nutzlast sparse.
 */
export function toAssignment(katalog: Katalog): CatalogAssignment {
  return {
    catalogId: katalog.id,
    ratings: (katalog.kenntnisse ?? [])
      .filter((k) => !!k.uuid && k.bewertung > 0)
      .map((k) => ({ itemUuid: k.uuid as string, rating: k.bewertung })),
  };
}
